import { SublocationData } from "@/sublocation/sublocation";
import "@/refrigerant/animated-border.css";
import { Card } from "@/components/ui/card";
import StaffOnly from "@/user/StaffOnly";
import { Suspense, useState } from "react";
import { GridPosition, hasPosition, InstallationData } from "@/installation/installation";
import { useSelectedOrganizationId } from "@/organization/api";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ChevronRight, Settings } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AssetGlyph } from "@/views/refrigerant/customer/AssetGlyph";
import { useHighlightAsset } from "@/sublocation/HighlightAssetContext";
import { useOpportunities } from "@/opportunity/api";
import { useEquipment } from "@/equipment/api";
import { SiteMapConfigureDialog } from "@/sublocation/SiteMapConfigureDialog";
import { Spinner } from "@/components/ui/spinner";

const AssetDialogContent = ({ installation, onCancel, onConfirm }: { installation: InstallationData; onCancel: () => void; onConfirm: () => void }) => {
  const status = installation.health_status;
  const { data: equipmentData } = useEquipment(installation.equipment);

  const displayName = equipmentData.name !== installation.name
    ? `${equipmentData.name} — ${installation.name}`
    : installation.name;

  if (status === "black") {
    return (
      <>
        <DialogHeader>
          <DialogTitle>{displayName}</DialogTitle>
          <DialogDescription>
            This equipment is not monitored by Thalo's refrigerant monitoring system.
          </DialogDescription>
        </DialogHeader>
        <p className="mt-4">
          To set up refrigerant monitoring for this equipment, please <a className="underline text-thalo-blueLight"
                                                                         href="mailto:hello@thalolabs.com">contact your
          account manager</a>.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Close</Button>
        </DialogFooter>
      </>
    )
  }

  const adjectives = {
    "red": "poorly",
    "yellow": "sub-optimally",
    "green": "well",
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{displayName}</DialogTitle>
        <DialogDescription>
          This equipment is performing <span className={`font-bold`}>{adjectives[status]}</span>.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
        <Button variant="thalo" onClick={onConfirm} className="w-full sm:w-auto">
          Go to full performance data
          <ChevronRight className="ml-2 h-4 w-4"/>
        </Button>
        <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">Cancel</Button>
      </DialogFooter>
    </>
  );
};

export const InstallationTile = ({ installation, disableTooltip = false, isSnoozed = false }: { installation: InstallationData; disableTooltip?: boolean; isSnoozed?: boolean }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const organizationId = useSelectedOrganizationId();
  const context = useHighlightAsset();

  const shouldHighlight = !context || context.assetToHighlight?.id === installation.id;

  // Fetch active issues count for this installation
  const { data: activeIssuesData } = useOpportunities({
    filters: {
      installation_ids: [installation.id],
      requires_action: true,
    },
    pageSize: 1, // We only need the count
  });

  const activeIssuesCount = activeIssuesData?.count ?? 0;

  const borderClass = {
    "red": "animated-border-bad",
    "yellow": "animated-border-unhealthy",
    "green": "animated-border-healthy",
    "black": "unmonitored-border"
  }

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleConfirm = () => {
    setDialogOpen(false);
    if (installation.health_status !== "black") {
      navigate({
        to: "/organization/$organizationId/locations/$locationId/equipment/$equipmentId",
        params: {
          organizationId: organizationId,
          equipmentId: installation.equipment,
          locationId: installation.location.id
        }
      });
    }
  };

  const getTooltipContent = () => {
    return (
      <div className="flex flex-col gap-1">
        <div className="font-semibold">{installation.name}</div>
        <div className="text-xs">
          {activeIssuesCount === 0 
            ? "No active issues" 
            : `${activeIssuesCount} ${activeIssuesCount === 1 ? 'active issue' : 'active issues'}`}
        </div>
      </div>
    );
  };

  const tileContent = (
    <div
      className={`relative aspect-square h-full w-full flex flex-col justify-center items-center p-2 ${disableTooltip ? '' : 'cursor-pointer'} ${borderClass[installation.health_status]} ${!shouldHighlight ? "opacity-20" : ""} ${isSnoozed ? "opacity-50 saturate-50" : ""}`}
      onClick={() => !disableTooltip && setDialogOpen(true)}
    >
      <AssetGlyph assetData={installation.asset}/>
    </div>
  );

  if (disableTooltip) {
    return tileContent;
  }

  return (
    <>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            {tileContent}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {getTooltipContent()}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <Suspense fallback={<Spinner />}>
            <AssetDialogContent installation={installation} onCancel={handleCancel} onConfirm={handleConfirm} />
          </Suspense>
        </DialogContent>
      </Dialog>
    </>
  )
}

const InstallationAtPosition = ({ installationsWithPositions, colIndex, rowIndex, disableTooltip = false, snoozedEquipmentIds }: {
  installationsWithPositions: (InstallationData & { position: GridPosition })[],
  colIndex: number,
  rowIndex: number,
  disableTooltip?: boolean,
  snoozedEquipmentIds?: Set<number>
}) => {

  const positions = installationsWithPositions.map(installation => installation.position)

  const xs = positions.map(position => position.grid_x);
  const ys = positions.map(position => position.grid_y);

  const minX = Math.min(...xs)
  const minY = Math.min(...ys)

  const positionIsAtCol = (colIndex: number, position: GridPosition) => {
    return position.grid_x - minX === colIndex;
  }

  const positionIsAtRow = (rowIndex: number, position: GridPosition) => {
    return position.grid_y - minY === rowIndex;
  }

  const installationAtPosition = installationsWithPositions
    .find(installation => positionIsAtCol(colIndex, installation.position) && positionIsAtRow(rowIndex, installation.position))

  if (!installationAtPosition) {
    return;
  }

  return (
    <InstallationTile installation={installationAtPosition} disableTooltip={disableTooltip} isSnoozed={snoozedEquipmentIds?.has(installationAtPosition.equipment)}/>
  )
}

export const SublocationMap = ({ installations, disableTooltip = false, snoozedEquipmentIds }: { installations: InstallationData[]; disableTooltip?: boolean; snoozedEquipmentIds?: Set<number> }) => {

  const installationsWithPositions = installations.filter(hasPosition);

  const positions = installationsWithPositions.map(installation => installation.position)

  const xs = positions.map(position => position.grid_x);
  const ys = positions.map(position => position.grid_y);

  const minX = Math.min(...xs)
  const maxX = Math.max(...xs)
  const minY = Math.min(...ys)
  const maxY = Math.max(...ys)

  const numColumns = maxX - minX + 1
  const numRows = maxY - minY + 1

  return (
    <div>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${numColumns}, minmax(0, 1fr))` }}>
        {
          Array(numRows).fill(0).map((_row, rowIndex) => (
            Array(numColumns).fill(0).map((_col, colIndex) => (
              <div
                className="col-span-1 flex items-center justify-center p-2"
                key={`${rowIndex}${colIndex}`}
              >
                <InstallationAtPosition
                  installationsWithPositions={installationsWithPositions}
                  colIndex={colIndex}
                  rowIndex={rowIndex}
                  disableTooltip={disableTooltip}
                  snoozedEquipmentIds={snoozedEquipmentIds}/>
              </div>
            ))
          ))
        }
      </div>
    </div>
  )
}

export const SublocationCard = ({ sublocation, snoozedEquipmentIds }: { sublocation: SublocationData; snoozedEquipmentIds?: Set<number> }) => {
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const installationsWithPositions = sublocation.installations.filter(installation => !!installation.position);

  // Get locationId from first installation (all installations in a sublocation share the same location)
  const locationId = sublocation.installations[0]?.location?.id;

  return (
    <Card>
      <div className="flex justify-between items-center p-3 lg:p-4">
        <h4 className="font-semibold text-lg">{sublocation.name}</h4>
        {locationId && (
          <StaffOnly>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setConfigDialogOpen(true)}
              className="text-gray-500 hover:text-gray-700"
            >
              <Settings className="w-4 h-4 mr-1" />
              Configure
            </Button>
          </StaffOnly>
        )}
      </div>
      <div className="flex justify-center bg-gray-50">
        {
          installationsWithPositions.length === 0 ? (
            <div className="bg-white m-4 p-6 lg:m-8 lg:p-8 flex flex-col items-center">
              <p className="text-gray-500 text-sm">Site map for this area is not yet configured.</p>
              {locationId && (
                <button
                  onClick={() => setConfigDialogOpen(true)}
                  className="text-thalo-blue hover:text-thalo-blue/80 text-sm underline mt-1"
                >
                  Click here to configure the layout
                </button>
              )}
            </div>
          ) : (
            <div className="w-fit h-fit bg-white bg-opacity-80 m-4 p-4 lg:m-8 lg:p-8">
              <Suspense fallback={<Spinner/>}>
                <SublocationMap installations={installationsWithPositions} snoozedEquipmentIds={snoozedEquipmentIds}/>
              </Suspense>
            </div>
          )
        }
      </div>
      {locationId && (
        <SiteMapConfigureDialog
          open={configDialogOpen}
          onOpenChange={setConfigDialogOpen}
          sublocationId={sublocation.id}
          locationId={locationId}
          installations={sublocation.installations}
        />
      )}
    </Card>
  )
}